/* eslint-disable @typescript-eslint/no-explicit-any */
// What the report page works out from a report before it draws it: the pure calculations, kept out of the components.

export const metrics = [{ key: 'ftp', label: 'TP' }, { key: 'hie', label: 'HIE' }, { key: 'pp', label: 'PP' }] as const;

export const completionPercent = (v: number | null) => (v === null || v === undefined ? null : Math.round(v * 100));
export const barWidth = (v: number | null) => `${Math.min(100, Math.max(0, (v ?? 0) * 100))}%`;

export const isStatusReport = (report: any) => report?.kind === 'STATUS';

/** How the training so far reads: encouraging about what has been done, never a verdict. */
export const adherenceText = (label: string, percent: number | null) => {
  const pct = percent === null ? '' : ` ${percent}% of`;
  switch (label) {
    case 'ON_PACE': return `You are keeping up with your prescribed training:${pct} the Low, High and Peak XSS so far.`;
    case 'SLIGHTLY_BEHIND': return `You have completed${pct} the prescribed training so far.`;
    case 'BEHIND': return `You have completed${pct} the prescribed training so far. Each session you add from here moves you closer.`;
    default: return 'Your progress will show here once you have completed some of your planned sessions.';
  }
};

/** Where the athlete is now (a plan in progress) or ended. */
export const fitnessNow = (report: any) => (isStatusReport(report) ? report?.status_report?.current : report?.fitness?.finish);

/** TP, HIE and PP from the start to now or the end, for the measures that have both. */
export const fitnessProgress = (report: any) =>
  metrics
    .map((m) => ({ label: m.label, scale: m.key === 'hie' ? 0.001 : 1, unit: m.key === 'hie' ? 'kJ' : 'W', start: report?.fitness?.start?.[m.key], finish: fitnessNow(report)?.[m.key] }))
    .filter((p) => p.start !== undefined && p.start !== null && p.finish !== undefined && p.finish !== null);

/** The breakthroughs, and the activities the athlete flagged (which no longer count as breakthroughs), in date order. */
export const breakthroughRows = (report: any) => {
  const items = report?.breakthroughs?.items ?? [];
  return [
    ...items.map((b: any) => ({ ...b, flaggedRow: false })),
    ...(report?.flagged_activities ?? [])
      .filter((f: any) => !items.some((b: any) => b.activity_id === f.activity_id))
      .map((f: any) => ({ activity_id: f.activity_id, name: f.name, date: f.date, flaggedRow: true })),
  ].sort((a: any, b: any) => String(a.date).localeCompare(String(b.date)));
};

/** A drop of more than 3% from the value before the activity is called out in the breakthroughs table. */
export const bigDrop = (b: any, key: string) => {
  const before = b.before?.[key];
  const after = b.after?.[key];
  return typeof before === 'number' && typeof after === 'number' && before > 0 && (after - before) / before < -0.03;
};

const startField: Record<string, string> = { TP: 'ftp', HIE: 'hie', PP: 'pp', 'Low load': 'low', 'High load': 'high', 'Peak load': 'peak' };

/** The rows of "where you are today": every projected target, with where the athlete is now beside it. */
export const projectionRows = (report: any) => {
  const status = report?.status_report;
  const focus = report?.focus_power;
  return (status?.projection?.requirements ?? []).map((r: any) => ({
    ...r,
    now: r.metric === 'Focus power' ? (focus?.finish ?? null) : startField[r.metric] ? (status?.current?.[startField[r.metric]] ?? null) : null,
  }));
};

/** The bars of "your primary goal": every target of the plan as last saved, from the start to where the athlete is or ended. */
export const progressRows = (report: any) => {
  const focus = report?.focus_power;
  const now = fitnessNow(report);
  return (report?.targets?.adjusted?.requirements ?? []).map((r: any) => {
    const field = startField[r.metric];
    const from = r.metric === 'Focus power' ? (focus?.start ?? null) : field ? (report.fitness?.start?.[field] ?? null) : null;
    const to = r.metric === 'Focus power' ? (focus?.finish ?? null) : field ? (now?.[field] ?? null) : null;
    return { label: r.label ?? r.metric, unit: r.unit, start: from, finish: to, target: r.target };
  });
};

export const primaryTitle = (report: any) => (report?.program_type === 'GOAL' ? 'Your Primary Goal' : report?.program_type === 'EVENT' ? 'Training-load readiness' : 'Your targets');

// Forecast journey: the measures a version of the plan predicted for the end, as the athlete reads them.
const fallbackLabels: Record<string, string> = { 'Focus power': 'Focus power', TP: 'Threshold Power (TP)', HIE: 'High Intensity Energy (HIE)', PP: 'Peak Power (PP)', 'Low load': 'Low Training Load', 'High load': 'High Training Load', 'Peak load': 'Peak Training Load' };

export const rowFor = (rows: any[] | undefined, metric: string) => (rows ?? []).find((r: any) => r.metric === metric);

export const journeyMetricsOf = (report: any): string[] =>
  Object.keys(report?.forecast_history?.versions?.[0]?.destination ?? {}).filter((m) => report.forecast_history.versions.some((v: any) => typeof v.destination[m] === 'number'));

export const journeyLabel = (report: any, metric: string) => rowFor(report?.targets?.original?.requirements, metric)?.label ?? fallbackLabels[metric] ?? metric;
export const journeyScale = (metric: string | null) => (metric === 'HIE' ? 0.001 : 1);
export const journeyUnit = (metric: string | null) => (metric === 'HIE' ? 'kJ' : metric?.endsWith('load') ? '' : 'W');
export const destinationUnit = (metric: string) => (metric === 'HIE' ? 'J' : metric.endsWith('load') ? '' : 'W');

export const journeyTarget = (report: any, metric: string | null) =>
  metric
    ? { original: rowFor(report?.targets?.original?.requirements, metric)?.target ?? null, adjusted: rowFor(report?.targets?.adjusted?.requirements, metric)?.target ?? null }
    : {};

/** What the plan came to at its end, for a finished plan. */
export const journeyActual = (report: any, metric: string | null) =>
  metric && !isStatusReport(report) && typeof rowFor(report?.targets?.original?.requirements, metric)?.actual === 'number'
    ? { date: report.lifecycle.planned_end_date, value: rowFor(report.targets.original.requirements, metric).actual }
    : null;
